import { doCalc15MinutesReminder } from '@entities/schedules/schedule/helpers.tsx';
import { getScheduleServiceDuration } from '@entities/schedules/service/helpers.ts';
import type { ScheduleTerm } from '@entities/schedules/term/index.ts';
import utcToZonedTime from 'date-fns-tz/utcToZonedTime';
import zonedTimeToUtc from 'date-fns-tz/zonedTimeToUtc';
import addDays from 'date-fns/addDays';
import addMinutes from 'date-fns/addMinutes';
import differenceInMinutes from 'date-fns/differenceInMinutes';
import endOfDay from 'date-fns/endOfDay';
import endOfWeek from 'date-fns/endOfWeek';
import format from 'date-fns/format';
import formatISO from 'date-fns/formatISO';
import isToday from 'date-fns/isToday';
import parseISO from 'date-fns/parseISO';
import startOfDay from 'date-fns/startOfDay';
import startOfWeek from 'date-fns/startOfWeek';
import subDays from 'date-fns/subDays';
import { useCallback, useEffect, useState } from 'react';
import { xhr } from '@shared/config';
import { doCheckArray } from '@shared/helpers/index.ts';
import { useDialog, usePopup, useUser } from '@shared/hooks';
import type { AvailablePeriod } from '@shared/types/available-periods';
import type { Schedule } from '@shared/types/schedules';
import { availablePeriodsApi, useAvailablePeriods } from '@src/entities/available-periods';
import { useSchedules, useTz } from '@src/entities/schedules';

export const useDate = (date: Date) => {
  const getWeekDays = useCallback(() => {
    const start = startOfWeek(date, { weekStartsOn: 1 });
    const week: Date[] = [];
    for (let i = 0; i < 7; i++) {
      week.push(addDays(start, i));
    }
    return week;
  }, [date]);

  const getHours = useCallback(() => {
    let start = startOfDay(date);
    const end = endOfDay(date);
    const hours: string[] = [];
    while (start < end) {
      hours.push(formatISO(start));
      start = addMinutes(start, 15);
    }
    return hours;
  }, [date]);

  return {
    week: getWeekDays(),
    hours: getHours(),
  };
};

export const useWeek = (date: Date) => {
  const getSessions = useCallback(() => {
    const start = startOfWeek(date, { weekStartsOn: 1 });
    const dates: Date[] = [];
    for (let i = 0; i < 7; i++) {
      dates.push(addDays(start, i));
    }
    const daysWithSessions = dates.map((day) => {
      const sessions = [];
      let start = startOfDay(day);
      const end = endOfDay(day);
      while (start < end) {
        const sessionStart = start;
        const sessionEnd = addMinutes(start, 15);
        sessions.push({ start: sessionStart, end: sessionEnd });
        start = addMinutes(start, 15);
      }
      return sessions;
    });
    return daysWithSessions;
  }, [date]);

  return {
    sessions: getSessions(),
  };
};

export const useWeekLoader = (date: Date) => {
  const { user } = useUser();

  const [isLoading, setIsLoading] = useState(false);

  const { setAvailablePeriods } = useAvailablePeriods();
  const { setSchedules } = useSchedules();

  useEffect(() => {
    const controller = new AbortController();
    const getSchedule = async () => {
      setIsLoading(true);
      const tz = 'Europe/Moscow';
      const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const timeZoneChanged = browserTz !== tz;

      const dateFormat = "yyyy-MM-dd'T'HH:mm";
      const start = format(
        subDays(
          timeZoneChanged
            ? zonedTimeToUtc(startOfWeek(date, { weekStartsOn: 1 }), tz)
            : startOfWeek(date, { weekStartsOn: 1 }),
          1
        ),
        dateFormat
      );
      const end = format(
        addDays(
          timeZoneChanged
            ? zonedTimeToUtc(endOfWeek(date, { weekStartsOn: 1 }), tz)
            : endOfWeek(date, { weekStartsOn: 1 }),
          1
        ),
        dateFormat
      );

      const params = {
        start_time: start,
        end_time: end,
        id: user?.id,
      };

      try {
        const availableRequest = xhr.get(`${availablePeriodsApi}`, {
          params: { ...params },
          signal: controller.signal,
        });
        const busyRequest = xhr.get(`/api/v1/schedules/schedule/`, {
          params: { ...params },
          signal: controller.signal,
        });

        const res = await Promise.all([availableRequest, busyRequest]);

        if (!res[0].data || !res[1].data) throw res;

        setAvailablePeriods(res[0].data);
        setSchedules(res[1].data);
      } catch (error) {
        console.log(error);
      }
      setIsLoading(false);
    };

    getSchedule();

    return () => {
      controller.abort();
    };
  }, [date, user]);

  return {
    isLoading,
  };
};

interface Session {
  start: Date;
  end: Date;
}

const findAvailablePeriod = (
  available: AvailablePeriod,
  sessionStart: Date,
  sessionEnd: Date,
  tz: string
) => {
  const availableStart = utcToZonedTime(parseISO(available.start_time), tz);
  const availableEnd = utcToZonedTime(parseISO(available.end_time), tz);
  const cloneSessionStart = structuredClone(sessionStart);
  const cloneSessionEnd = structuredClone(sessionEnd);
  const startReminder = doCalc15MinutesReminder(availableStart);
  const endReminder = doCalc15MinutesReminder(availableEnd);
  const diff = differenceInMinutes(sessionEnd, availableStart);

  if (diff <= 15) {
    cloneSessionStart.setMinutes(cloneSessionStart.getMinutes() + startReminder);
    cloneSessionEnd.setMinutes(cloneSessionEnd.getMinutes() + startReminder);
  } else if (diff > 15) {
    let needToMinus = 0;
    endReminder === 5 && (needToMinus = 10);
    endReminder === 10 && (needToMinus = 5);
    cloneSessionEnd.setMinutes(cloneSessionEnd.getMinutes() - needToMinus - startReminder);
  }

  // console.log('calcDiff: ', calcDifferents(startAvailable));
  // console.log('sessionStart: ', sessionStart);
  // console.log('cloneStart: ', cloneStart);

  // console.log('SE:', cloneSessionEnd);
  // console.log('AE:', endAvailable);
  // console.log(
  //   '======================================================================================'
  // )
  return availableStart <= cloneSessionStart && availableEnd >= cloneSessionEnd;
};

type FindAvailableReturnType = {
  firstAvailablePeriod: AvailablePeriod | undefined;
  secondAvailablePeriod: AvailablePeriod | undefined;
};

export const useSession = () => {
  const { availablePeriods } = useAvailablePeriods();
  const { schedules } = useSchedules();
  const { tz } = useTz();
  /**
   * findAvailable - крч в одной 15-ти минутной ячейке может быть два периода (заканчиваться один, начинаться второй).
   * Поэтому возвращается два периода.
   */
  const findAvailable = useCallback(
    (session: Session): FindAvailableReturnType => {
      const { start: sessionStart, end: sessionEnd } = session;

      let firstAvailablePeriod;
      doCheckArray(availablePeriods) &&
        (firstAvailablePeriod = availablePeriods.find((available) =>
          findAvailablePeriod(available, sessionStart, sessionEnd, tz)
        ));

      let secondAvailablePeriod;
      // в целях оптимизации я сначала проверяю есть ли первый, только потом ищу второй
      firstAvailablePeriod &&
        (secondAvailablePeriod = [...availablePeriods]
          .reverse()
          .find((available) => findAvailablePeriod(available, sessionStart, sessionEnd, tz)));

      return {
        firstAvailablePeriod,
        secondAvailablePeriod,
      };
    },

    [availablePeriods, tz]
  );

  const findBusy = useCallback(
    (session: Session): Schedule | undefined => {
      if (schedules.length === 0) return;
      const { start, end } = session;
      return schedules.find((busy) => {
        return (
          utcToZonedTime(parseISO(busy.start_time), tz) <= start &&
          utcToZonedTime(parseISO(busy.end_time), tz) >= end
        );
      });
    },
    [schedules, tz]
  );

  return { findAvailable, findBusy };
};

interface AvailableCreate extends Omit<AvailablePeriod, 'address'> {
  address: number[];
}

export const useEmpty = (
  // mode: string,
  term?: ScheduleTerm,
  onSettingsError?: () => void,
  onAddressError?: () => void
) => {
  // const { user } = useUser();
  // const { activeAddressId } = useAddresses();
  const { popup } = usePopup();
  const { notification } = useDialog();
  const { tz } = useTz();
  const { addAvailablePeriod } = useAvailablePeriods();
  // const onlineSettingsFilled = user?.consultation_cost && user.consultation_duration;
  // const offlineSettingsFilled =
  //   user?.consultation_offline_cost && user.consultation_offline_duration;

  const duration = getScheduleServiceDuration(term?.services) || 1;
  // let address: number[] = [];

  // if (mode === 'ON')
  // duration = user?.consultation_duration ? parseFloat(user.consultation_duration) : 0;

  // if (mode === 'OF') {
  // duration = user?.consultation_offline_duration
  //   ? parseFloat(user.consultation_offline_duration)
  //   : 0;
  // address = activeAddressId ? [activeAddressId] : [];
  // }

  const createEmpty = async (session: { start: Date; end: Date }) => {
    // if (mode === 'ON' && !onlineSettingsFilled && onSettingsError) return onSettingsError();
    //
    // if (mode === 'OF' && !offlineSettingsFilled && onSettingsError) return onSettingsError();
    //
    if (!term) {
      const message = {
        title: 'Настройки',
        text: 'Не выбрано условие',
        confirmText: 'Ок',
      };

      return notification(message);
    }

    const timeFormat = "yyyy-MM-dd'T'HH:mm:ssxxx";
    const newAvailable = {
      start_time: format(zonedTimeToUtc(session.start, tz), timeFormat),
      end_time: format(zonedTimeToUtc(addMinutes(session.start, duration), tz), timeFormat),
      // status: [mode],
      terms: [term?.id],
      // address: address,
    };

    try {
      const res = await xhr.post<AvailablePeriod>(
        `/api/v1/schedules/available_period/`,
        newAvailable
      );

      if (!res.data) throw res;

      addAvailablePeriod(res.data);
      popup({ type: 'success', text: 'График создан' });
    } catch (error) {
      console.log(error);
      popup({ type: 'error', text: 'Не удалось создать график' });
    }
  };

  const onEmptyClick = (session: { start: Date; end: Date }) => createEmpty(session);
  return {
    onEmptyClick,
  };
};

export const useAvailable = (term?: ScheduleTerm, onSuccess?: () => void) => {
  const { setAvailablePeriod, removeAvailablePeriod } = useAvailablePeriods();
  // const { activeAddressId } = useAddresses();
  const { popup } = usePopup();
  // const { user } = useUser();

  // const updatePeriod = async (availablePeriod: AvailablePeriod) => {
  //   const status = availablePeriod.terms[0].status;
  //
  //   const updatedPeriod: AvailableCreate = {
  //     ...availablePeriod,
  //     status,
  //     terms: [term?.id],
  //   };
  //
  //   try {
  //     if (!term) return;
  //     if (user?.consultation_duration !== user?.consultation_offline_duration && !hasMode) return;
  //
  // const res = await xhr.patch<AvailablePeriod>(
  //   `/api/v1/schedules/available_period/${availablePeriod.id}/`,
  //   updatedPeriod,
  // );
  // if (!res.data) throw res;
  //
  // setAvailablePeriod(res.data);
  // popup({ type: 'success', text: 'График обновлен' });
  // } catch (error) {
  //   console.log(error);
  //   popup({ type: 'error', text: 'Не удалось обновить график' });
  // }
  // };

  const removePeriod = async (availablePeriod: AvailablePeriod) => {
    removeAvailablePeriod(availablePeriod.id);
    try {
      await xhr.delete(`/api/v1/schedules/available_period/${availablePeriod.id}/`);
      onSuccess && onSuccess();
      popup({ type: 'success', text: 'График удален' });
    } catch (error) {
      console.log(error);
      popup({ type: 'error', text: 'Не удалось удалить график' });
    }
  };

  // const onAvailableClick = (availablePeriod: AvailablePeriod) => {
  //   removePeriod(availablePeriod);
  //   // const availablePeriodTermId = availablePeriod.status.findIndex((status) => status === mode) !== -1;
  //   // const availablePeriodTermId = availablePeriod.terms[0].id;
  //   // availablePeriodTermId === term?.id ? updatePeriod(availablePeriod) : removePeriod(availablePeriod);
  // };

  return {
    onAvailableClick: removePeriod,
  };
};
export const isNow = (date: Date) => {
  let now = false;
  if (isToday(date)) {
    const current = new Date();
    const end = addMinutes(date, 30);
    now = date <= current && current <= end;
  }
  return now;
};

export const isNow15 = (date: Date) => {
  let now = false;
  if (isToday(date)) {
    const current = new Date();
    const end = addMinutes(date, 15);
    now = date <= current && current <= end;
  }
  return now;
};
